import React, { useCallback, useEffect, useRef } from "react";
import dayjs from "dayjs";
import Flatpickr, {
  DateTimePickerHandle,
  DateTimePickerProps,
} from "react-flatpickr";
import styled, { createGlobalStyle, useTheme } from "styled-components";
import { Rounded } from "../theme/Rounded";
import { InputConfigDate } from "../model/form/dto/InputDateConfigDto";
import { Flex } from "./Flex";
import { FontSize, Text } from "./Text";
import { LabelDirection } from "./shared/types/SharedLabelProps";
import map from "lodash/map";
import { Clickable } from "./Clickable";
import { Spacing } from "../theme/Spacing";
import { Trans, useTranslation } from "react-i18next";
import { add } from "date-fns";
import {
  formatDateKey,
  formatDateKeyInZone,
  mergeHourRanges,
  splitHourRange,
} from "../utils/Date";

const ALT_INPUT_CLASS = "atira-date-picker-flat-alt-input";

const CalendarStyles = createGlobalStyle`
  /* altInput:true swaps in a separate, plain <input> the user actually sees
   * and types into - it isn't a descendant of StyledDatePicker below, so its
   * own CSS never reaches it. Style it here instead. */
  .${ALT_INPUT_CLASS} {
    width: 100%;
    height: 2.3rem;
    box-sizing: border-box;
    font-family: inherit;
    font-size: 1rem;
    color: ${({ theme }) => theme.textColor};
    background-color: ${({ theme }) => theme.white};
    border: 1px solid ${({ theme }) => theme.lightgray};
    border-radius: ${Rounded.md};
    padding-inline: ${Spacing.s};
    outline: none !important;
    cursor: pointer;

    &::placeholder {
      color: ${({ theme }) => theme.darkerSub};
    }

    &:hover,
    &:focus {
      border-color: ${({ theme }) => theme.main};
    }
  }

  .flatpickr-weekdays,
  .flatpickr-months 
  .flatpickr-month,
  .flatpickr-monthDropdown-months,
  .flatpickr-weekday, .flatpickr-day.selected {
    background-color: ${({ theme }) => theme.main} !important;
  }

  .flatpickr-calendar.arrowBottom::after {
    border-top-color: ${({ theme }) => theme.white};
  }

  .flatpickr-day.selected {
    border-color: ${({ theme }) => theme.main} !important;
  }

  .flatpickr-weekday {
    color: ${({ theme }) => theme.white} !important;
  }

  .flatpickr-monthDropdown-month {
    background-color: ${({ theme }) => theme.white} !important;
    color: ${({ theme }) => theme.main} !important;
  }

  .flatpickr-monthDropdown-month:hover {
    background-color: ${({ theme }) => theme.darkSub} !important;
  }

  .flatpickr-prev-month:hover svg, .flatpickr-next-month:hover svg {
    fill: ${({ theme }) => theme.darkSub} !important;
  }

  .form-control.input:focus-visible {
    outline: none;
  }
`;

const HoursWrapper = styled(Flex)`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fill, minmax(5.5rem, 1fr));
  gap: ${Spacing.xs};
`;

const OptionalSpan = styled.span`
  color: ${({ theme }) => theme.darkerSub};
  font-size: ${FontSize.s};
`;

const HourSquare = styled(Clickable)<{ selected: boolean; disabled: boolean }>`
  width: 100%;
  height: 2rem;
  border: 1px solid ${({ theme }) => theme.lightgray};
  border-radius: ${Rounded.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xs};
  box-sizing: border-box;
  font-size: ${FontSize.s};
  transition: all 0.2s;
  background-color: ${({ theme, selected }) =>
    selected ? theme.lightMain : theme.white};
  color: ${({ theme, selected }) => theme[selected ? "white" : "textColor"]};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  ${({ theme, disabled }) =>
    !disabled
      ? `
   &:hover {
    color: ${theme.sub};
    border-color: ${theme.main};
    background-color: ${theme.lightMain};
  }
  `
      : ""}
`;

/**
 * Anchors `time` ("HH:mm") to `date`'s calendar day in `timezone`, rather
 * than mutating hours/minutes on the browser's local clock - the latter
 * silently shifts the stored instant whenever the visitor isn't in the
 * module's configured timezone.
 *
 * `date` must already be an absolute instant anchored in `timezone` (e.g.
 * `props.value`, itself previously built by this function or by
 * `buildDateFromPickerSelection` below) - reading a *raw* Flatpickr
 * selection's day this way is wrong, see that function's comment.
 */
function buildDateAtTime(date: Date, time: string, timezone?: string): Date {
  const day = timezone
    ? dayjs(date).tz(timezone).format("YYYY-MM-DD")
    : dayjs(date).format("YYYY-MM-DD");

  return (
    timezone ? dayjs.tz(`${day} ${time}`, timezone) : dayjs(`${day} ${time}`)
  ).toDate();
}

/**
 * Anchors `time` to the calendar day the visitor actually clicked in
 * Flatpickr. Flatpickr emits browser-local midnight for the clicked day -
 * it has no notion of the input's configured timezone - so that day must be
 * read via the browser's own local calendar components, never re-viewed
 * through `timezone` first: doing so can roll the day backward or forward
 * across midnight and silently book the wrong day (e.g. a Tokyo browser
 * picking Jan 2 for a New York booking becomes Jan 1).
 */
function buildDateFromPickerSelection(
  date: Date,
  time: string,
  timezone?: string,
): Date {
  const day = dayjs(date).format("YYYY-MM-DD");

  return (
    timezone ? dayjs.tz(`${day} ${time}`, timezone) : dayjs(`${day} ${time}`)
  ).toDate();
}

/**
 * Flatpickr's altFormat only ever formats a Date through the browser's own
 * local clock - it has no notion of the input's configured timezone, so the
 * visible alt-input text silently showed the viewer's local time instead of
 * the field's (e.g. a booking saved as 18:00 in the field's timezone showed
 * as 17:00 to a viewer whose browser is one hour behind). Overwriting the
 * alt-input's text directly, via dayjs in the configured timezone, fixes
 * the display without touching flatpickr's own date-selection/calendar
 * logic - only the text shown is corrected.
 */
function writeTimezoneCorrectAltInputText(
  instance:
    | { altInput?: HTMLInputElement | null; selectedDates: Date[] }
    | undefined,
  timezone: string | undefined,
  hasTime: boolean,
) {
  const date = instance?.selectedDates[0];

  if (!date || !instance?.altInput) {
    return;
  }

  const zoned = timezone ? dayjs(date).tz(timezone) : dayjs(date);

  instance.altInput.value = hasTime
    ? zoned.format("D MMMM, YYYY - hh:mm A")
    : zoned.format("D MMMM, YYYY");
}

const isDayAllowed = (days: string[], date: Date) => {
  // No days configured means "bookable any day" everywhere else in this
  // feature (isCustomDateInput, the backend validators) - without this, an
  // hours-only booking config disables every date in the calendar.
  if (!days.length) {
    return true;
  }

  const allDaysAsIndexes = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ]
    .map((day, index) => {
      if (days.includes(day)) {
        return index;
      }

      return undefined;
    })
    .filter((day) => day !== undefined);

  const day = date.getDay();
  return allDaysAsIndexes.includes(day);
};

const StyledDatePicker = styled(Flatpickr)<{ valid?: boolean }>`
  border: 1px solid
    ${({ theme, valid }) => theme[valid ? "lightgray" : "danger"]};
  height: 2.3rem;
  border-radius: ${Rounded.md};
  padding-inline: ${Spacing.s};

  &:focus {
    border: 1px solid ${({ theme }) => theme.lightgray};
    box-shadow: none;
  }

  &:hover {
    border: 1px solid ${({ theme }) => theme.lightgray};
  }
`;


type AtiraDatePickerFlatProps = {
  enableTime?: boolean;
  onChange: (selectedDates: Date | null) => void;
  valid?: boolean;
  config?: InputConfigDate;
  labelDirection?: LabelDirection;
  errorMessage?: string;
  possibleBookedSlots?: Record<string, Record<string, string[]>>;
  dataTestId?: string;
} & DateTimePickerProps;

export const AtiraDatePickerFlat: React.FC<AtiraDatePickerFlatProps> = ({
  enableTime,
  onChange,
  valid = false,
  title,
  config,
  errorMessage,
  dataTestId,
  ...props
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const pickerRef = useRef<DateTimePickerHandle>(undefined);

  const hasTime = Boolean(enableTime || config?.hours);

  // react-flatpickr re-syncs the underlying instance whenever `value`
  // changes (e.g. a parent re-render producing a new-but-equal Date
  // object), which can silently re-run Flatpickr's own browser-local
  // altFormat and undo the correction below - redoing it in an effect,
  // scoped to the same inputs react-flatpickr itself reacts to, is what
  // makes it stick regardless of how often that happens, without running
  // on every unrelated render (which was destabilizing the open calendar's
  // own DOM - see git history). The onReady/onValueUpdate props passed to
  // StyledDatePicker below cover the same correction for the very first
  // paint, before this effect's first run.
  useEffect(() => {
    writeTimezoneCorrectAltInputText(
      pickerRef.current?.flatpickr,
      config?.timezone,
      hasTime,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, config?.timezone, hasTime]);

  const takenHoursForDay = React.useMemo(() => {
    if (!props.value) {
      return [];
    }

    const tz = config?.timezone || "UTC";
    const date = props.value as Date;
    const key = tz?.length
      ? formatDateKeyInZone(date, tz)
      : formatDateKey(date);

    const taken = new Set<string>(
      (props.possibleBookedSlots?.[props.name as string]?.[key] as any) || [],
    );

    return taken.size ? Array.from(taken) : [];
  }, [props.value, props.possibleBookedSlots, props.name, config?.timezone]);

  const splitHours = config?.hours
    ? mergeHourRanges(config.hours)
        .map((range) => splitHourRange(range, config?.slotDurationMinutes || 30))
        .flat()
    : [];

  const onPickDate = useCallback(
    (date: Date | null) => {
      if (!date) {
        onChange(null);
        return;
      }

      if (config?.hours?.length) {
        const allHours = Object.values(config.hours);
        const from = allHours[0]?.from;
        onChange(
          from ? buildDateFromPickerSelection(date, from, config.timezone) : date,
        );
        return;
      }

      onChange(date);
    },
    [config?.hours, config?.timezone, onChange],
  );

  const onSelectHour = (from: string) => {
    if (!props.value) {
      return;
    }

    onChange(buildDateAtTime(props.value as Date, from, config?.timezone));
  };

  // A fresh `{...}` object literal every render (particularly `maxDate: new
  // Date()`, always a new instance) gives react-flatpickr's own options a
  // new reference on every unrelated render - including ones unrelated to
  // this field entirely, e.g. a sibling field changing in a large edit
  // form. react-flatpickr re-applies (and visibly redraws) the instance
  // whenever that reference changes, which both re-ran Flatpickr's own
  // browser-local altFormat (clobbering the correction above) and
  // destabilized an already-open calendar popup enough to make Playwright's
  // day-cell clicks flaky. Memoizing on the values that actually matter
  // keeps the reference - and the underlying instance - stable otherwise.
  const maxDate = React.useMemo(() => add(new Date(), { days: 60 }), []);
  const altInputClass = React.useMemo(
    () =>
      [ALT_INPUT_CLASS, dataTestId ? `${dataTestId}-alt-input` : ""]
        .filter(Boolean)
        .join(" "),
    [dataTestId],
  );
  const flatpickrOptions = React.useMemo(
    () => ({
      ...props.options,
      enable: [(date: Date) => isDayAllowed(config?.days || [], date)],
      dateFormat: hasTime ? "Y-m-d - H:i" : "Y-m-d",
      altFormat: hasTime ? "J F, Y - H:i K" : "J F, Y",
      altInput: true,
      // altInput:true swaps in a separate visible input and turns this one
      // type="hidden" - data-testid above stays on the hidden one, so the
      // clickable/visible input needs its own styling and test hooks.
      altInputClass,
      minDate: "today",
      maxDate,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.options, config?.days?.join(","), hasTime, altInputClass, maxDate],
  );

  return (
    <Flex flexDirection="column" gap="xs">
      <Flex gap="xs">
        {title ? (
          <label
            style={{
              color: theme.black,
              cursor: props?.id ? "pointer" : "auto",
              width: "fit-content",
              fontSize: FontSize.m,
              margin:
                props.labelDirection === LabelDirection.HORIZONTAL
                  ? "auto"
                  : "0",
            }}
          >
            {title}
          </label>
        ) : null}
        {props.required ? (
          <span style={{ color: theme.danger }}> *</span>
        ) : (
          <OptionalSpan> ({t("common.optional")})</OptionalSpan>
        )}
      </Flex>
      <StyledDatePicker
        ref={pickerRef}
        data-enable-time={enableTime}
        data-testid={dataTestId}
        valid={valid}
        autoSave="true"
        required={props.required}
        options={flatpickrOptions}
        {...props}
        value={props.value || ""}
        onChange={([date]) => onPickDate(date || null)}
        // Corrects the alt-input's displayed text to the field's own
        // timezone - altFormat alone can't do this, see
        // writeTimezoneCorrectAltInputText. react-flatpickr only wires up
        // hooks passed as top-level props like this one (same as onChange
        // above); nesting them inside `options` is silently ignored. onReady
        // covers the initial value (editing an existing booking);
        // onValueUpdate covers every subsequent pick.
        onReady={(_selectedDates, _dateStr, instance) =>
          writeTimezoneCorrectAltInputText(instance, config?.timezone, hasTime)
        }
        onValueUpdate={(_selectedDates, _dateStr, instance) =>
          writeTimezoneCorrectAltInputText(
            instance,
            config?.timezone,
            hasTime,
          )
        }
      />

      {config?.hours ? (
        <HoursWrapper>
          {map(splitHours, ({ from, to }, i) => (
            <HourSquare
              dataTestId={
                dataTestId ? `${dataTestId}-hour-${from}` : undefined
              }
              disabled={
                !valid || !props.value || takenHoursForDay.includes(from)
              }
              onClick={() => onSelectHour(from)}
              selected={
                props.value
                  ? (config?.timezone
                      ? dayjs(props.value as Date).tz(config.timezone)
                      : dayjs(props.value as Date)
                    ).format("HH:mm") === from
                  : false
              }
              key={`${from.toString()}-${to.toString()}-${i}`}
            >
              {from}
            </HourSquare>
          ))}
        </HoursWrapper>
      ) : null}

      <Text fontSize="xs" align="center" color="darkerSub">
        <Trans
          i18nKey={"common.dateInTimeZone"}
          values={{ tz: config?.timezone || "UTC" }}
          components={[<strong />]}
        />
      </Text>

      {errorMessage && valid === false ? (
        <Text color="danger" fontSize="s">
          {errorMessage}
        </Text>
      ) : null}

      <CalendarStyles />
    </Flex>
  );
};
